import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Check, Phone, Mail, Clock, Car, MessageSquare, Instagram, Facebook, Youtube, MapPin, Menu, X } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import QualificationForm from "../components/QualificationForm";

const LOGO_URL = "/logo.png";
const HERO_IMAGE = "https://customer-assets-0z36b82j.emergentagent.net/job_404047a2-5b3a-4a5c-970f-2293d8b931fa/artifacts/klo3ym38_Web5.webp";
const FEATURE_IMAGE_2 = "https://customer-assets.emergentagent.com/job_trish-honda-crm/artifacts/paufabj3_web2%20copy.webp";
const FEATURE_IMAGE_3 = "https://customer-assets.emergentagent.com/job_trish-honda-crm/artifacts/u4x6w3c4_Web3%20copy.webp";
const FEATURE_IMAGE_4 = "https://customer-assets.emergentagent.com/job_trish-honda-crm/artifacts/zdjamct2_Web4%20copy.webp";

const Header = ({ isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            {logoFailed ? (
              <span className="font-heading font-bold text-[#E91E8C] text-lg md:text-xl" data-testid="header-logo-fallback">
                Trish Drives Honda
              </span>
            ) : (
              <img
                src={LOGO_URL}
                alt="Trish Drives Honda"
                className="h-14 md:h-16 object-contain"
                onError={() => setLogoFailed(true)}
                data-testid="header-logo-img"
              />
            )}
          </Link>
          
          <nav className="hidden md:flex items-center gap-2">
            <Link to="/" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Home</Link>
            <Link to="/cars" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Cars</Link>
            <a href="#booking-form" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Book Visit</a>
          </nav>
          
          {/* Mobile: Book Now + Burger Menu */}
          <div className="flex items-center gap-3">
            <a href="#booking-form" className="btn-primary text-sm py-3 px-6" data-testid="header-book-btn">
              Book Now
            </a>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-[#E91E8C] text-white"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="flex flex-col p-4 space-y-2">
            <Link 
              to="/" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-3 rounded-lg font-medium transition-all duration-200"
            >
              Home
            </Link>
            <Link 
              to="/cars" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-3 rounded-lg font-medium transition-all duration-200"
            >
              Cars
            </Link>
            <a 
              href="#booking-form" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-3 rounded-lg font-medium transition-all duration-200"
            >
              Book Visit
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

const HeroSection = () => {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);
  return (
    <section
      className="relative min-h-screen flex items-end pb-12 pt-20 overflow-hidden"
      data-testid="hero-section"
      style={heroFailed ? {
        background: 'linear-gradient(135deg, #1A1A1A 0%, #2C2420 50%, #E91E8C 100%)'
      } : undefined}
    >
      {/* Black gradient from bottom - 30% height */}
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 15%, rgba(0,0,0,0.4) 25%, transparent 35%)' }}></div>
      {!heroFailed && (
        <div className="absolute inset-0 bg-[#1A1A1A]">
          <img 
            src={HERO_IMAGE} 
            alt="Trish with Honda"
            loading="eager"
            onLoad={() => setHeroLoaded(true)}
            onError={() => setHeroFailed(true)}
            className={`w-full h-full object-cover object-[25%_center] md:object-center transition-opacity duration-500 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
            data-testid="hero-image"
          />
        </div>
      )}
      
      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Main content - Heading with description below */}
        <div className="max-w-2xl mb-8 slide-up">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" style={{ textShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
            Your Dream Honda<br />
            <span className="text-white">Awaits You</span>
          </h1>
          <p className="text-white/90 text-sm md:text-base leading-relaxed">
            Experience the perfect blend of reliability, style, and performance. 
            Let Trish guide you to your ideal Honda vehicle with personalized service and expert advice.
          </p>
        </div>
        
        {/* Buttons row - evenly spaced */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
          <a href="#booking-form" className="btn-primary text-center" data-testid="hero-book-btn">
            Book a Showroom Visit
          </a>
          <Link to="/cars" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[#E91E8C] text-center" data-testid="hero-browse-btn">
            Browse Available Cars
          </Link>
        </div>
        
        {/* Trust badges */}
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2 text-white/90">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium">Fast Response</span>
          </div>
          <div className="flex items-center gap-2 text-white/90">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium">Easy Booking</span>
          </div>
          <div className="flex items-center gap-2 text-white/90">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium">New Models Available</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const VehicleCarousel = ({ vehicles }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(3);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSlidesPerView(1);
      } else if (window.innerWidth < 1024) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-slide effect with loop
  useEffect(() => {
    if (isPaused || vehicles.length <= slidesPerView) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        // Loop back to start when reaching the end
        if (prev >= vehicles.length - slidesPerView) {
          return 0;
        }
        return prev + 1;
      });
    }, 3000); // Slide every 3 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, vehicles.length, slidesPerView]);

  const maxIndex = Math.max(0, vehicles.length - slidesPerView);

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      if (prev >= maxIndex) return 0;
      return prev + 1;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      if (prev <= 0) return maxIndex;
      return prev - 1;
    });
  };

  const formatPrice = (price, currency = "JMD", status = "available") => {
    if (status === "special_order") {
      return "Special Order";
    }
    if (!price || price === 0) {
      return "Contact for Price";
    }
    
    if (currency === "JMD") {
      // Format as JMD with 'm' suffix for millions
      if (price >= 1000000) {
        const millions = price / 1000000;
        return `JMD $${millions.toFixed(3)}m`;
      }
      return `JMD $${price.toLocaleString()}`;
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (vehicle) => {
    if (vehicle.status === "coming_soon") {
      return (
        <div className="absolute top-4 right-4 bg-[#E91E8C] text-white text-xs font-medium px-3 py-1 rounded-full">
          Coming Soon
        </div>
      );
    }
    if (vehicle.status === "special_order") {
      return (
        <div className="absolute top-4 right-4 bg-purple-600 text-white text-xs font-medium px-3 py-1 rounded-full">
          Special Order
        </div>
      );
    }
    return null;
  };

  if (!vehicles.length) return null;

  return (
    <section className="py-20 md:py-32 bg-[#1A1A1A]" data-testid="vehicle-carousel-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Available Vehicles
            </h2>
            <p className="text-lg text-gray-400">
              Explore our selection of premium Honda vehicles
            </p>
          </div>
          
          <div className="hidden md:flex gap-2">
            <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full border-2 border-[#E91E8C] flex items-center justify-center text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white transition-colors"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full border-2 border-[#E91E8C] flex items-center justify-center text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white transition-colors"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div 
          className="overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * (100 / slidesPerView)}%)` }}
          >
            {vehicles.map((vehicle) => (
              <div 
                key={vehicle.vehicle_id} 
                className="flex-shrink-0 px-3"
                style={{ width: `${100 / slidesPerView}%` }}
              >
                <div className="card-vehicle h-full" data-testid={`vehicle-card-${vehicle.vehicle_id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#FFF5F9] to-[#FCE4EC]">
                    <img
                      src={vehicle.images[0]}
                      alt={vehicle.name}
                      onError={(e) => {
                        // Hide broken image; show fallback icon layer
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextElementSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div
                      className="absolute inset-0 flex-col items-center justify-center text-[#E91E8C]/40"
                      style={{ display: 'none' }}
                      data-testid={`vehicle-image-fallback-${vehicle.vehicle_id}`}
                    >
                      <Car className="w-16 h-16" />
                      <span className="mt-2 text-xs font-medium text-[#6B5E55]">Image unavailable</span>
                    </div>
                    {getStatusBadge(vehicle)}
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                      {vehicle.name}
                    </h3>
                    <p className="text-2xl font-bold text-[#E91E8C] mb-4">
                      {formatPrice(vehicle.price, vehicle.currency, vehicle.status)}
                    </p>
                    <a 
                      href="#booking-form" 
                      className="block w-full text-center btn-secondary py-3"
                      data-testid={`book-vehicle-${vehicle.vehicle_id}`}
                    >
                      {vehicle.status === "special_order" ? "Inquire Now" : "Book Test Drive"}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: Math.ceil(vehicles.length - slidesPerView + 1) }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex === idx ? 'w-8 bg-[#E91E8C]' : 'bg-gray-500 hover:bg-gray-400'
              }`}
              data-testid={`carousel-dot-${idx}`}
            />
          ))}
        </div>
        
        <div className="flex md:hidden justify-center gap-2 mt-4">
          <button
            onClick={prevSlide}
            className="w-12 h-12 rounded-full border-2 border-[#E91E8C] flex items-center justify-center text-[#E91E8C]"
            data-testid="carousel-prev-mobile-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="w-12 h-12 rounded-full border-2 border-[#E91E8C] flex items-center justify-center text-[#E91E8C]"
            data-testid="carousel-next-mobile-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  return (
    <section className="py-20 md:py-32 bg-white" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Content on LEFT, Image on RIGHT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left side - Content */}
          <div className="text-left order-2 lg:order-1">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
              Why Choose Trish?
            </h2>
            <p className="text-lg text-[#4A4A4A] mb-8">
              Experience personalized car buying with expert guidance every step of the way
            </p>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#E91E8C]/10 flex items-center justify-center flex-shrink-0">
                  <Car className="w-7 h-7 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                    Premium Selection
                  </h3>
                  <p className="text-[#4A4A4A]">
                    Access to the latest Honda models with competitive pricing and financing options.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#E91E8C]/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-7 h-7 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                    Personal Service
                  </h3>
                  <p className="text-[#4A4A4A]">
                    One-on-one consultation to find the perfect vehicle that fits your lifestyle.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#E91E8C]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-7 h-7 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                    Quick Process
                  </h3>
                  <p className="text-[#4A4A4A]">
                    Streamlined buying experience from test drive to delivery in record time.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Image */}
          <div className="relative rounded-3xl overflow-hidden h-[500px] order-1 lg:order-2">
            <img 
              src={FEATURE_IMAGE_4} 
              alt="Trish with Honda" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "Do you have the Honda Elevate in stock?",
      answer: (
        <div>
          <p className="mb-3">Yes, the Honda Elevate is available.</p>
          <p className="mb-3">Current availability includes units from:</p>
          <ul className="list-disc list-inside mb-3">
            <li>March – April 2026</li>
            <li>June 2026 arrivals</li>
          </ul>
          <p>Availability may vary, so early booking is recommended.</p>
        </div>
      )
    },
    {
      question: "Do you have 2026 Honda vehicles?",
      answer: (
        <p>Yes! We currently have 2026 model Honda vehicles available.</p>
      )
    },
    {
      question: "What Honda models do you currently have?",
      answer: (
        <div>
          <p className="mb-3">Our available Honda lineup includes:</p>
          <ul className="list-disc list-inside mb-3">
            <li>Honda Elevate</li>
            <li>Honda HR-V</li>
            <li>Honda BR-V</li>
            <li>Honda CR-V</li>
            <li>Honda Ridgeline</li>
            <li>Honda Pilot Elite</li>
          </ul>
          <p>For full specifications, features, and images, please visit the <Link to="/cars" className="text-[#E91E8C] font-semibold hover:underline">Cars section</Link>.</p>
        </div>
      )
    },
    {
      question: "How can I check vehicle details?",
      answer: (
        <div>
          <p className="mb-3">You can view:</p>
          <ul className="list-disc list-inside mb-3">
            <li>Features</li>
            <li>Interior & exterior photos</li>
            <li>Model options</li>
            <li>Vehicle highlights</li>
          </ul>
          <p>directly on the website under the <Link to="/cars" className="text-[#E91E8C] font-semibold hover:underline">Cars section</Link>.</p>
        </div>
      )
    },
    {
      question: "How do I reserve or book one?",
      answer: (
        <div>
          <p className="mb-3">Simply book a:</p>
          <ul className="list-disc list-inside mb-3">
            <li>Showroom Visit</li>
            <li>Test Drive</li>
          </ul>
          <p>through the website, and a representative will contact you to secure your vehicle.</p>
        </div>
      )
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-[#FFF0F5]" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-[#4A4A4A]">
            Get answers to common questions about our Honda vehicles
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className={`rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                openIndex === index 
                  ? 'bg-white border-[#E91E8C]/30 shadow-lg' 
                  : 'bg-white/70 border-[#E91E8C]/10 hover:bg-white hover:border-[#E91E8C]/20'
              }`}
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors"
                data-testid={`faq-button-${index}`}
              >
                <span className={`font-heading text-lg font-semibold pr-4 transition-colors ${
                  openIndex === index ? 'text-[#E91E8C]' : 'text-[#1A1A1A]'
                }`}>
                  {faq.question}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  openIndex === index ? 'bg-[#E91E8C] rotate-180' : 'bg-[#E91E8C]/10'
                }`}>
                  <ChevronDown className={`w-5 h-5 transition-colors ${
                    openIndex === index ? 'text-white' : 'text-[#E91E8C]'
                  }`} />
                </div>
              </button>
              
              {/* Accordion Content with Grid Animation */}
              <div 
                className="grid transition-all duration-300 ease-in-out"
                style={{ 
                  gridTemplateRows: openIndex === index ? '1fr' : '0fr'
                }}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-6 text-[#4A4A4A] leading-relaxed border-t border-[#E91E8C]/10 pt-4 mx-4">
                    {faq.answer}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-[#4A4A4A] mb-4">Still have questions?</p>
          <a 
            href="#booking-form" 
            className="btn-primary inline-block"
            data-testid="faq-book-btn"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#1A1A1A] text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Trish Drives Honda" className="h-20 mb-6" />
            <p className="text-gray-400 mb-6 max-w-md">
              Your trusted partner in finding the perfect Honda vehicle. Experience premium service and expert guidance.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E91E8C] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E91E8C] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#E91E8C] transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/cars" className="text-gray-400 hover:text-white transition-colors">Cars</Link>
              </li>
              <li>
                <a href="#booking-form" className="text-gray-400 hover:text-white transition-colors">Book Visit</a>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-gray-400">
                <Phone className="w-4 h-4" />
                <span>1876-564-4901</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Mail className="w-4 h-4" />
                <span>twong@atlautomotive.com</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-4 h-4 mt-1" />
                <span>Kingston, Jamaica</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-12 pt-8 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Trish Drives Honda. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API}/vehicles`, {
          params: { show_on_home: true, status: "available" }
        });
        setVehicles(response.data);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      }
    };
    fetchVehicles();
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header isScrolled={isScrolled} />
      <HeroSection />
      <section id="booking-form" className="py-20 md:py-32 bg-[#FFF5F9]" data-testid="booking-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
              Book Your Visit
            </h2>
            <p className="text-lg text-[#4A4A4A] mb-4">
              Answer a few quick questions and we'll tailor the perfect Honda experience for you
            </p>
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#E91E8C]/30 text-sm text-[#4A4A4A]">
              <MapPin className="w-4 h-4 text-[#E91E8C]" />
              <span>Showroom visits & test drives at <strong>ATL Hagley Park, Kingston</strong></span>
            </div>
          </div>
          <QualificationForm vehicles={vehicles} context="landing" />
        </div>
      </section>
      <VehicleCarousel vehicles={vehicles} />
      <FeaturesSection />
      <FAQSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
